import moment from "moment";
import api from "./Api";
import { formatAppointmentDateTime } from "../utils/dateUtils";
import { handleApiError } from "../utils/apiUtils";
import { RATE_TYPE } from "../common/Constant";
import { formatFieldName } from "../utils/formatFieldName";
import { formatCurrency } from "../utils/formatCurrency";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

const billingConfig = process.env.REACT_APP_PICKLIST_URL || "";
const billingUserConfig = process.env.REACT_APP_USER_MGMT_URL || "";
if (!billingConfig) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_CASE_URL in your environment variables.",
  );
}

const BillingConfigurationAPI = api(billingConfig);

const userConfigAPI = api(billingUserConfig);

const fetchUerListByTenantID = async (searchText: string) => {
  try {
    const response = await userConfigAPI.get(
      `/api/user/list/by-tenant?searchString=${searchText}`,
    );
    return response.data ?? [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchUerListByCase = async (params: any) => {
  try {
    const response = await userConfigAPI.post("/api/user/list/by-case", params);
    return response.data ?? [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchUerListByTeam = async (params: any) => {
  try {
    const response = await userConfigAPI.post("/api/user/list/by-team", params);
    return response.data ?? [];
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getBillingConfigurationGrid = async (billingData: any): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.post<ApiResponse<any>>(
      "/api/billing/config/search",
      billingData,
    );

    const getRateTypeLabel = (type: string) => {
      switch (type) {
        case RATE_TYPE.FLAT:
          return "Fixed";
        case RATE_TYPE.PER_PAGE:
          return "Per Page";
        case RATE_TYPE.PER_LINE:
          return "Per Line";
        default:
          return "-";
      }
    };

    const getClientRate = (data: any) => {
      const value =
        data.clientRateTypeFlatRate ||
        data.clientRateTypeFlatRatePerPage ||
        data.clientRateTypeFlatRatePerLine ||
        data.clientRateTypeFlatRatePerHour;

      return value > 0 ? `$ ${value}` : "-";
    };

    const getRate = (data: any) => {
      const value = data.rateTypeFlatRatePerHour || data.rate;

      return value ? formatCurrency(value, data.currency) : "-";
    };

    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      effectiveFrom: formatAppointmentDateTime(data.effectiveFrom),
      effectiveTo: formatAppointmentDateTime(data.effectiveTo),
      lastUpdatedOn: formatAppointmentDateTime(data.lastUpdatedOn),

      rateType: getRateTypeLabel(data.rateType),
      rate: getRate(data),
      clientRate: getClientRate(data),

      actions: { Edit: "Edit" },
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getBillingConfigurationGridView = async (
  billingData: any,
): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.post<ApiResponse<any>>(
      "/api/billing/config/search",
      billingData,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      effectiveFrom: formatAppointmentDateTime(data.effectiveFrom),
      effectiveTo: formatAppointmentDateTime(data.effectiveTo),
      lastUpdatedOn: formatAppointmentDateTime(data.lastUpdatedOn),
      rateType:
        data.rateType === RATE_TYPE.FLAT
          ? "Fixed"
          : data.rateType === RATE_TYPE.PER_PAGE
            ? "Per Page"
            : data.rateType === RATE_TYPE.PER_LINE
              ? "Per Line"
              : "",
      actions: { View: "View" },
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getCaseLevelMasterBillingList = async (
  billingData: any,
): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.post<ApiResponse<any>>(
      "/api/billing/case-config/search",
      billingData,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      lastUpdatedOn: formatAppointmentDateTime(data.lastUpdatedOn),
      rateType:
        data.rateType === RATE_TYPE.FLAT
          ? "Fixed"
          : data.rateType === RATE_TYPE.PER_PAGE
            ? "Per Page"
            : data.rateType === RATE_TYPE.PER_LINE
              ? "Per Line"
              : "",
      thresholdType:
        data.thresholdType === "Flat" ? "Fixed" : data.thresholdType,
      status: data.isActive == 1 ? "Active" : "InActive",
      actions: { View: "View" },
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getBillingSummary = async (): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.get<ApiResponse<any>>(
      "/api/billing/config/summary",
    );

    return response.data?.result;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const fetchBillingConfigurationDetails = async (
  billingConfigId: any,
): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.get(
      `/api/billing/config/details?BillingConfigId=${billingConfigId}`,
    );
    const data = response.data?.result;

    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const addBillingConfig = async (
  billingData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await BillingConfigurationAPI.post(
      "/api/billing/config/add",
      billingData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateBillingConfig = async (
  billingData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await BillingConfigurationAPI.put(
      "/api/billing/config/update",
      billingData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getBillingConfigChangeLog = async (
  billingConfigId: any,
): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.get<ApiResponse<any>>(
      `/api/billing/config/changelog?billingConfigId=${billingConfigId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      fieldLabelUpdated: formatFieldName(data.fieldName),
      changedOn: formatAppointmentDateTime(data.changedOn),
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getCaseLevelBillingList = async (billingData: any): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.post<ApiResponse<any>>(
      "/api/billing/case-config/search",
      billingData,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      lastUpdatedOn: formatAppointmentDateTime(data.lastUpdatedOn),
      actions: { View: "View" },
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
const addCaseLevelBillingConfig = async (
  billingData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await BillingConfigurationAPI.post(
      "/api/billing/case-config/add",
      billingData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const updateCaseLevelBillingConfig = async (
  billingData: any,
): Promise<ApiResponse<any>> => {
  try {
    const response = await BillingConfigurationAPI.put(
      "/api/billing/case-config/update",
      billingData,
    );

    return response.data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getCaseLevelBillingDetails = async (
  billingCaseConfigId: any,
): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.get(
      `/api/billing/case-config/details/by-id?billingCaseConfigId=${billingCaseConfigId}`,
    );
    const data = response.data?.result;

    return data;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};
const getCaseLevelBillingChangeLog = async (
  billingCaseConfigId: any,
): Promise<any> => {
  try {
    const response = await BillingConfigurationAPI.get<ApiResponse<any>>(
      `/api/billing/case-config/changelog?billingCaseConfigId=${billingCaseConfigId}`,
    );
    const updatedResult = response.data?.result.map((data: any) => ({
      ...data,
      fieldLabelUpdated: formatFieldName(data.fieldUpdated),
      updatedOn: formatAppointmentDateTime(data.updatedOn),
      fieldName: data?.fieldUpdated,
    }));

    return updatedResult;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getDoctorListByClient = async (clientID: any) => {
  try {
    const { data } = await BillingConfigurationAPI.get(
      `/api/billing/config/doctorlist/by-clientid?clientId=${clientID}`,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((item: any) => ({
      value: item.userId,
      label: item.userInformation,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getUserListByRoleAndClient = async (clientID: any, roleId: any) => {
  try {
    const { data } = await BillingConfigurationAPI.get(
      `/api/billing/config/userlist/by-roleid?clientId=${clientID}&roleId=${roleId}`,
    );
    const result = data?.result ?? [];
    const filterData = result?.map((item: any) => ({
      value: item.userId,
      label: item.userInformation,
    }));
    return filterData;
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

export {
  fetchUerListByTenantID,
  getBillingConfigurationGrid,
  getBillingConfigurationGridView,
  getCaseLevelMasterBillingList,
  getBillingSummary,
  fetchBillingConfigurationDetails,
  addBillingConfig,
  updateBillingConfig,
  getBillingConfigChangeLog,
  getCaseLevelBillingList,
  addCaseLevelBillingConfig,
  updateCaseLevelBillingConfig,
  getCaseLevelBillingDetails,
  getCaseLevelBillingChangeLog,
  fetchUerListByCase,
  fetchUerListByTeam,
  getDoctorListByClient,
  getUserListByRoleAndClient,
};
